
export const TICKET_PLATFORMS = ['web', 'ios', 'android'] as const;
export type TicketPlatform = typeof TICKET_PLATFORMS[number];

export const TICKET_SEVERITIES = ['low', 'normal', 'high', 'critical'] as const;
export type TicketSeverity = typeof TICKET_SEVERITIES[number];

export const TICKET_STATUSES = ['new', 'in_progress', 'waiting_on_user', 'resolved'] as const;
export type TicketStatus = typeof TICKET_STATUSES[number];

export function normalizeTicketStatus(status?: string | null): TicketStatus {
    if (status && (TICKET_STATUSES as readonly string[]).indexOf(status) !== -1) {
        return status as TicketStatus;
    }
    return 'new';
}

export const TICKET_TYPES = ['bug', 'task', 'feature'] as const;
export type TicketType = typeof TICKET_TYPES[number];

/** Staff-only internal workflow. Not shown to clients. */
export const TICKET_WORKFLOW_STAGES = [
    'backlog',
    'investigating',
    'in_development',
    'ready_for_test',
    'awaiting_deployment',
] as const;
export type TicketWorkflowStage = typeof TICKET_WORKFLOW_STAGES[number];

/** Active work sections on the In progress board (excludes backlog). */
export const TICKET_ACTIVE_WORKFLOW_STAGES = TICKET_WORKFLOW_STAGES.filter(
    (stage) => stage !== 'backlog',
) as TicketWorkflowStage[];

export function normalizeTicketWorkflowStage(stage?: string | null): TicketWorkflowStage | null {
    if (!stage) {
        return null;
    }
    if ((TICKET_WORKFLOW_STAGES as readonly string[]).indexOf(stage) !== -1) {
        return stage as TicketWorkflowStage;
    }
    return null;
}

export type TicketBoardTab = 'new' | 'backlog' | 'in_progress' | 'resolved';

export const TICKET_SOURCES = ['chat'] as const;
export type TicketSource = typeof TICKET_SOURCES[number];
export type TicketUpdateVisibility = 'internal' | 'user';
export type TicketUpdateKind = 'message' | 'status';
export type TicketAwaitingReply = 'staff' | 'user';
export type TicketAttachmentType = 'image' | 'video';

export interface TicketAttachment {
    url: string;
    fileName: string;
    type: TicketAttachmentType;
}

export interface TicketPlatformMap {
    web?: boolean | null;
    ios?: boolean | null;
    android?: boolean | null;
}

export interface TicketUpdate {
    id?: string;
    kind?: TicketUpdateKind;
    message: string;
    createdBy: string;
    createdByName: string;
    creationDate: string;
    visibility: TicketUpdateVisibility;
    status?: TicketStatus;
    attachments?: TicketAttachment[];
}

export function isTicketStatusUpdate(update: TicketUpdate): boolean {
    return update?.kind === 'status' || (!update?.kind && !!update?.status);
}

/** All affected user ids, with legacy single-field fallback. Safe for old tickets. */
export function ticketAffectedUserIds(ticket: Partial<SupportTicket> | null | undefined): string[] {
    if (!ticket) {
        return [];
    }
    if (Array.isArray(ticket.affectedUserIds) && ticket.affectedUserIds.length) {
        return [...new Set(ticket.affectedUserIds.filter((id): id is string => !!id))];
    }
    const legacy = ticket.affectedUserId || ticket.userId;
    return legacy ? [legacy] : [];
}

/** Reporter + all affected users (for My Tickets / notifications). */
export function ticketParticipantIds(ticket: Partial<SupportTicket> | null | undefined): string[] {
    if (!ticket) {
        return [];
    }
    return [...new Set([ticket.reportedById, ...ticketAffectedUserIds(ticket)].filter((id): id is string => !!id))];
}

export class SupportTicket {
    id?: string;
    /** Human-readable incremental id assigned server-side (not the Firestore doc id). */
    ticketNumber?: number;
    title?: string;
    description?: string;
    type: TicketType = 'bug';
    platforms: TicketPlatformMap = {};
    severity: TicketSeverity = 'normal';
    status: TicketStatus = 'new';
    /** Staff-only stage for scheduling work. Clients never see this. */
    workflowStage?: TicketWorkflowStage | null;
    userId?: string;
    reportedById?: string;
    /** Primary affected user (first in affectedUserIds). Kept for older tickets / consumers. */
    affectedUserId?: string;
    /** All users impacted by this ticket. Prefer this; fall back to affectedUserId/userId. */
    affectedUserIds?: string[];
    /** Reporter and affected users, so My Tickets can query either role. */
    participantIds?: string[];
    /** Set when the reporter created the ticket themselves. Admins can still change the affected user. */
    reporterLocked?: boolean;
    source?: TicketSource;
    sourceId?: string;
    conversationId?: string;
    assigneeId?: string | null;
    attachments?: TicketAttachment[];
    createdBy?: string;
    creationDate?: string;
    updatedAt?: string;
    updatedBy?: string;
    lastMessage?: string;
    lastMessageAt?: string;
    lastMessageBy?: string;
    lastMessageByName?: string;
    awaitingReply?: TicketAwaitingReply | null;
    updates?: {
        [updateId: string]: TicketUpdate;
    };

    constructor(init?: Partial<SupportTicket>) {
        Object.assign(this, init);
    }

    selectedPlatforms(): TicketPlatform[] {
        return TICKET_PLATFORMS.filter((platform) => !!this.platforms?.[platform]);
    }

    getUpdates(): TicketUpdate[] {
        if (!this.updates) {
            return [];
        }
        return Object.keys(this.updates)
            .map((id) => ({ ...this.updates[id], id }))
            .sort((a, b) => (a.creationDate || '').localeCompare(b.creationDate || ''));
    }

    activityAt(): string {
        return this.lastMessageAt || this.updatedAt || this.creationDate || '';
    }

    getAffectedUserIds(): string[] {
        return ticketAffectedUserIds(this);
    }

    primaryAffectedUserId(): string {
        return this.getAffectedUserIds()[0] || '';
    }

    includesParticipant(userId: string): boolean {
        if (!userId) {
            return false;
        }
        return ticketParticipantIds(this).indexOf(userId) !== -1;
    }

    normalizedStatus(): TicketStatus {
        return normalizeTicketStatus(this.status);
    }

    normalizedWorkflowStage(): TicketWorkflowStage | null {
        return normalizeTicketWorkflowStage(this.workflowStage);
    }

    /** Which admin board tab this ticket belongs on. */
    boardTab(): TicketBoardTab {
        const status = this.normalizedStatus();
        const stage = this.normalizedWorkflowStage();
        if (status === 'resolved') {
            return 'resolved';
        }
        if (stage === 'backlog') {
            return 'backlog';
        }
        if (status === 'new') {
            return 'new';
        }
        return 'in_progress';
    }
}

export class  ProductFeature {
    title?: string;
    description?: string;
    creationDate?: string;
    createdBy?: string;
    id?: string;
    lastModifiedBy? : string;
    updatedAt?: string;
    updatedBy?: string;
    feature?: any;
    status?: "active" | "complete";
    video?: any;
    comments?: {
        [commentId: string]: any;
    }
    votes?: {
        [userID: string]: {
            timestamp: string,
            userName: string
        }
    }
    
    constructor (init: Partial<ProductFeature>){
        Object.assign(this, init);
    }

    getVoters() {
        const toReturn = [];

        if(this.votes) {
          const voterIds = Object.keys(this.votes);
          for (const voterId of voterIds) {
            const voter = this.votes[voterId];
            toReturn.push({
              ...voter,
              userId: voterId
            });
          }
        }

        return toReturn
    }
  
    getTotalVotes() {
        return this.votes? Object.keys(this.votes).length : 0;
    }

    isVoter(userId) {
        return this.votes && !!this.votes[userId]
    }
  }


export class  ProductUpdate {
    title?: string;
    description?: string;
    creationDate?: string;
    createdBy?: string;
    id?: string;
    lastModifiedBy? : string;
    video?: any;
    likes?: {
        [userID: string]: {
            timestamp: string,
            userName: string
        }
    }
    
    constructor (init: Partial<ProductUpdate>){
        Object.assign(this, init);
    }
  
    getTotalLikes() {
        return this.likes? Object.keys(this.likes).length : 0;
    }

    isLiker(userId) {
        return this.likes && !!this.likes[userId]
    }
  }